/**
 * ============================================================================
 *  File        : frontend/src/data/navigation.ts
 *  Module      : data
 *  Purpose     : 管理台主導覽與模組狀態定義
 *  Author      : Phoenix Lai
 *  Created     : 2026-05-30
 *  Updated     : 2026-05-30
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 */

import type { ModuleDefinition, ResourceConfig } from '../types'
import { PERMISSIONS } from '../lib/permissions'

/**
 * 主導覽（spec §8.2 top-level）。permissionKey 為 null 代表登入即可見；
 * 其餘依 usePermissions().can() gating，無權限隱藏。master-data 受
 * master.price.manage gating。
 */
export const modules: ModuleDefinition[] = [
  {
    id: 'dashboard',
    path: '/',
    labelKey: 'nav.dashboard',
    descriptionKey: 'desc.dashboard',
    status: 'ready',
    icon: 'LayoutDashboard',
    counters: ['dashboard.metricCases', 'dashboard.metricShipments'],
    permissionKey: null,
  },
  {
    id: 'cases',
    path: '/cases',
    labelKey: 'nav.cases',
    descriptionKey: 'desc.cases',
    status: 'partial',
    icon: 'FolderKanban',
    counters: ['field.caseNo', 'case.modules'],
    permissionKey: PERMISSIONS.caseRead,
  },
  {
    id: 'reports',
    path: '/reports',
    labelKey: 'nav.reports',
    descriptionKey: 'desc.reports',
    status: 'partial',
    icon: 'BarChart3',
    counters: ['report.progress', 'report.settlement'],
    permissionKey: PERMISSIONS.reportRead,
  },
  {
    id: 'audit',
    path: '/audit',
    labelKey: 'nav.audit',
    descriptionKey: 'desc.audit',
    status: 'ready',
    icon: 'ShieldCheck',
    counters: ['dashboard.metricAudit', 'field.status'],
    permissionKey: PERMISSIONS.auditRead,
  },
  {
    id: 'admin-users',
    path: '/admin/users',
    labelKey: 'nav.adminUsers',
    descriptionKey: 'desc.admin',
    status: 'ready',
    icon: 'Users',
    counters: ['admin.users'],
    permissionKey: PERMISSIONS.adminUserManage,
  },
  {
    id: 'admin-roles',
    path: '/admin/roles',
    labelKey: 'nav.adminRoles',
    descriptionKey: 'desc.adminRoles',
    status: 'ready',
    icon: 'ShieldCheck',
    counters: ['admin.roles'],
    permissionKey: PERMISSIONS.adminRoleManage,
  },
  // 主檔群組（spec §B）：品目 / 素材 / 場地 / 台秤 / 倉庫 拆成 5 個獨立全 CRUD 入口，
  // 全部受 system.configs.manage（PERMISSIONS.masterPriceManage）gating。
  {
    id: 'master-categories',
    path: '/admin/master/categories',
    labelKey: 'master.categories',
    descriptionKey: 'desc.masterData',
    status: 'ready',
    icon: 'Database',
    counters: ['master.categories'],
    permissionKey: PERMISSIONS.masterPriceManage,
  },
  {
    id: 'master-materials',
    path: '/admin/master/materials',
    labelKey: 'master.materials',
    descriptionKey: 'desc.masterData',
    status: 'ready',
    icon: 'Database',
    counters: ['master.materials'],
    permissionKey: PERMISSIONS.masterPriceManage,
  },
  {
    id: 'master-venues',
    path: '/admin/master/venues',
    labelKey: 'master.venues',
    descriptionKey: 'desc.masterData',
    status: 'ready',
    icon: 'Database',
    counters: ['master.venues'],
    permissionKey: PERMISSIONS.masterPriceManage,
  },
  {
    id: 'master-scales',
    path: '/admin/master/scales',
    labelKey: 'master.scales',
    descriptionKey: 'desc.masterData',
    status: 'ready',
    icon: 'Database',
    counters: ['master.scales'],
    permissionKey: PERMISSIONS.masterPriceManage,
  },
  {
    id: 'master-warehouses',
    path: '/admin/master/warehouses',
    labelKey: 'master.warehouses',
    descriptionKey: 'desc.masterData',
    status: 'ready',
    icon: 'Database',
    counters: ['master.warehouses'],
    permissionKey: PERMISSIONS.masterPriceManage,
  },
  {
    // 二要素認証（2FA）設定 — 任何登入使用者皆可管理自己的 2FA（permissionKey: null）。
    id: 'security',
    path: '/admin/security',
    labelKey: 'nav.security',
    descriptionKey: 'desc.security',
    status: 'ready',
    icon: 'ShieldCheck',
    counters: ['twofa.title'],
    permissionKey: null,
  },
]

export const masterResources: ResourceConfig[] = [
  {
    id: 'categories',
    titleKey: 'master.categories',
    descriptionKey: 'resource.master.description',
    listPath: '/api/v1/master/categories',
    createPath: '/api/v1/master/categories',
    updatePath: '/api/v1/master/categories/:id',
    idKey: 'id',
    primaryActionKey: 'common.create',
    emptyKey: 'common.empty',
    columns: ['id', 'code', 'name', 'is_active'],
    fields: [
      { key: 'code', labelKey: 'field.code', kind: 'text', required: true },
      { key: 'name', labelKey: 'field.name', kind: 'text', required: true },
      { key: 'parent_id', labelKey: 'field.parentId', kind: 'number' },
      { key: 'category_type', labelKey: 'field.categoryType', kind: 'text' },
      { key: 'sort_order', labelKey: 'field.sortOrder', kind: 'number' },
      { key: 'is_active', labelKey: 'field.status', kind: 'checkbox' },
    ],
  },
  {
    id: 'materials',
    titleKey: 'master.materials',
    descriptionKey: 'resource.master.description',
    listPath: '/api/v1/master/materials',
    createPath: '/api/v1/master/materials',
    updatePath: '/api/v1/master/materials/:id',
    idKey: 'id',
    primaryActionKey: 'common.create',
    emptyKey: 'common.empty',
    columns: ['id', 'material_code', 'name', 'category_id', 'is_active'],
    fields: [
      { key: 'material_code', labelKey: 'field.materialCode', kind: 'text', required: true },
      { key: 'category_id', labelKey: 'master.categories', kind: 'number' },
      { key: 'name', labelKey: 'field.name', kind: 'text', required: true },
      { key: 'scientific_name', labelKey: 'field.scientificName', kind: 'text' },
      { key: 'default_uom', labelKey: 'field.defaultUom', kind: 'text' },
      { key: 'is_active', labelKey: 'field.status', kind: 'checkbox' },
    ],
  },
  {
    id: 'venues',
    titleKey: 'master.venues',
    descriptionKey: 'resource.master.description',
    listPath: '/api/v1/master/venues',
    createPath: '/api/v1/master/venues',
    updatePath: '/api/v1/master/venues/:id',
    idKey: 'id',
    primaryActionKey: 'common.create',
    emptyKey: 'common.empty',
    columns: ['id', 'venue_code', 'name', 'organization_id', 'is_active'],
    fields: [
      { key: 'venue_code', labelKey: 'field.venueCode', kind: 'text', required: true },
      { key: 'name', labelKey: 'field.name', kind: 'text', required: true },
      { key: 'organization_id', labelKey: 'field.organizationId', kind: 'number' },
      { key: 'country_code', labelKey: 'field.countryCode', kind: 'text' },
      { key: 'address', labelKey: 'field.address', kind: 'textarea' },
      { key: 'is_active', labelKey: 'field.status', kind: 'checkbox' },
    ],
  },
  {
    id: 'scales',
    titleKey: 'master.scales',
    descriptionKey: 'resource.master.description',
    listPath: '/api/v1/master/scales',
    createPath: '/api/v1/master/scales',
    updatePath: '/api/v1/master/scales/:id',
    idKey: 'id',
    primaryActionKey: 'common.create',
    emptyKey: 'common.empty',
    columns: ['id', 'scale_code', 'name', 'venue_id', 'is_active'],
    fields: [
      { key: 'scale_code', labelKey: 'field.scaleCode', kind: 'text', required: true },
      { key: 'name', labelKey: 'field.name', kind: 'text', required: true },
      { key: 'organization_id', labelKey: 'field.organizationId', kind: 'number' },
      { key: 'venue_id', labelKey: 'field.venueId', kind: 'number' },
      { key: 'scale_type', labelKey: 'field.scaleType', kind: 'text' },
      { key: 'is_active', labelKey: 'field.status', kind: 'checkbox' },
    ],
  },
  {
    id: 'warehouses',
    titleKey: 'master.warehouses',
    descriptionKey: 'resource.master.description',
    listPath: '/api/v1/master/warehouses',
    createPath: '/api/v1/master/warehouses',
    updatePath: '/api/v1/master/warehouses/:id',
    idKey: 'id',
    primaryActionKey: 'common.create',
    emptyKey: 'common.empty',
    columns: ['id', 'warehouse_code', 'name', 'organization_id', 'is_bonded'],
    fields: [
      { key: 'warehouse_code', labelKey: 'field.warehouseCode', kind: 'text', required: true },
      { key: 'name', labelKey: 'field.name', kind: 'text', required: true },
      { key: 'organization_id', labelKey: 'field.organizationId', kind: 'number' },
      { key: 'venue_id', labelKey: 'field.venueId', kind: 'number' },
      { key: 'country_code', labelKey: 'field.countryCode', kind: 'text' },
      { key: 'address', labelKey: 'field.address', kind: 'textarea' },
      { key: 'is_bonded', labelKey: 'field.isBonded', kind: 'checkbox' },
    ],
  },
]
